import { Accessor, createEffect, onCleanup } from "solid-js";
import { SessionState } from "../types/SessionState";

type Msg =
  | { type: "status"; status: "listening" | "ended" }
  | {
      type: "utterance";
      id: string;
      speaker: 1 | 2;
      text?: string;
      finalText?: boolean;
      translation?: string;
      translationError?: boolean;
      confidence?: number;
      ts?: number;
      lang?: string;
    }
  | { type: "config"; languages: { s1: string; s2: string } };

export function useSessionBroadcast(opts: {
  sessionId?: string | null;
  uiSession: Accessor<SessionState>; 
  sessionStatus: Accessor<"draft" | "in_progress" | "completed">;
  interpreterEnabled: Accessor<boolean>;
  sourceLang: Accessor<string>;
  targetLang: Accessor<string | "">;
}) {
  let bc: BroadcastChannel | null = null;

  createEffect(() => {
    const id = opts.sessionId;
    try {
      bc?.close();
    } catch {}
    bc = id ? new BroadcastChannel(`sess-${id}`) : null;
  });

  // publish status based on UI session transitions
  createEffect(() => {
    if (!bc) return;
    const s = opts.uiSession();
    if (s === SessionState.Listening || s === SessionState.Connected) {
      bc.postMessage({ type: "status", status: "listening" } satisfies Msg);
    }
    if (s === SessionState.Idle && opts.sessionStatus() === "completed") {
      bc.postMessage({ type: "status", status: "ended" } satisfies Msg);
    }
  });

  // publish interpreter config whenever languages/mode change
  createEffect(() => {
    if (!bc) return;
    const enabled = opts.interpreterEnabled();
    const s1 = opts.sourceLang() || "en-US";
    const s2 = opts.targetLang() || "";
    const payload = { s1, s2 };
    if (enabled) {
      bc.postMessage({ type: "config", languages: payload } satisfies Msg);
    }
  });

  function publishUtterance(
    evt: Omit<Extract<Msg, { type: "utterance" }>, "type">
  ) {
    if (!bc) return;
    try {
      bc.postMessage({ type: "utterance", ...evt } satisfies Msg);
    } catch {}
  }

  onCleanup(() => {
    try {
      bc?.close();
    } catch {}
    bc = null;
  });

  return { publishUtterance };
}
